import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DocumentModel, UPLOADSTATUS } from 'src/app/models/document/document.model';
import { BulkUploadService } from 'src/app/services/bulk-upload.service';
import { ToastService } from 'src/app/services/toast.service';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SubmitBtnComponent } from '../submit-btn/submit-btn.component';
import { from, Subscription } from 'rxjs';
import { mergeMap, tap, finalize } from 'rxjs/operators';

@Component({
    selector: 'app-bulk-upload-documents',
    templateUrl: './bulk-upload-documents.component.html',
    styleUrls: ['./bulk-upload-documents.component.scss'],
    standalone: true,
    imports: [
      CommonModule,
      NgxDropzoneModule,
      MatIconModule,
      MatButtonModule,
      MatProgressBarModule,
      MatTooltipModule,
      SubmitBtnComponent
    ]
})
export class BulkUploadDocumentsComponent {

  // Map to track upload subscriptions for each file (for cancel functionality)
  private uploadSubscriptions = new Map<DocumentModel, Subscription>();

  constructor(private bulkUploadService: BulkUploadService, private toastService: ToastService) {

  }
  MAX_SIZE = 50000000; // 50MB

  @Input() disabled: boolean;
  @Input() isMulti: boolean = true;
  @Input() maxFiles: number = 30; // Maximum number of files that can be uploaded
  @Input() maxConcurrentUploads: number = 5; // Maximum number of concurrent file uploads
  @Input() acceptedFileTypes: string = '.pdf,.docx'; // Accepted file types
  @Input() submitDisabled: boolean = true; // Submit button disabled state
  @Input() submitLoading: boolean = false; // Submit button loading state
  @Input() submitText: string = 'Submit'; // Submit button text
  @Input() organizationType: string = ''; // Organization type (e.g. 'provider', 'lender')
  @Output() uploadingEvent = new EventEmitter<boolean>();
  @Output() onFileRemove = new EventEmitter<number>();
  @Output() onSubmit = new EventEmitter<void>();

  public uploadedFiles() : any[] {
    return this.files.filter(file => file.uploadStatus === UPLOADSTATUS.SUCCESS).map((f) => ({ name: f.file.name, serverFileName: f.serverFileName }));
  }

  // Dropzone file uplaod funcitons
  @Input()files: DocumentModel[] = [];

  onSelect(event: any) {
    if (!this.isMulti) {
      this.files = [];
    }

    // Check if adding new files would exceed the max file limit
    // Only count files that haven't been successfully uploaded yet
    const currentFileCount = this.activeFileCount;
    const newFileCount = event.addedFiles.length;
    const totalFiles = currentFileCount + newFileCount;

    if (totalFiles > this.maxFiles) {
      const allowedFiles = this.maxFiles - currentFileCount;
      this.toastService.error(
        `You can only upload a maximum of ${this.maxFiles} files. You have ${currentFileCount} file(s) queued. You can add ${allowedFiles} more file(s).`,
        'File Limit Exceeded'
      );
      return;
    }

    // Just add files to the list - don't upload yet!
    // Files will be uploaded when startUpload() is called from parent component
    event.addedFiles.forEach((file: File) => {
      const documentDetails = new DocumentModel(file, UPLOADSTATUS.QUEUED);
      this.files.push(documentDetails);
    });

    if (event?.rejectedFiles?.length > 0) {
      this.toastService.error(`Please upload a valid file (PDF, DOCX). Maximum ${this.maxFiles} files can be uploaded at a time`, 'Invalid File');
    }
  }

  /**
   * Public method to start uploading all queued files
   * Called from parent component when Submit button is clicked
   * @param organizationId - The organization ID from the selected dropdown
   */
  public startUpload(organizationId?: string): void {
    // Filter files that haven't been uploaded yet
    // Include both QUEUED (new files) and ERROR (failed files to retry)
    const filesToUpload = this.files.filter(f =>
      f.uploadStatus === UPLOADSTATUS.QUEUED || f.uploadStatus === UPLOADSTATUS.ERROR
    );

    if (filesToUpload.length === 0) {
      return;
    }

    const orgIdDisplay = organizationId ? `[OrgId: ${organizationId}]` : '[No OrgId]';

    // Set all files to PENDING (actively uploading) and store organizationId
    filesToUpload.forEach(f => {
      f.uploadStatus = UPLOADSTATUS.PENDING;
      f.organizationId = organizationId; // Store org ID for retry functionality
    });

    // Emit uploading started
    this.uploadingEvent.emit(true);

    // Upload files with controlled concurrency
    from(filesToUpload)
      .pipe(
        mergeMap(
          (documentDetails) => {
            const uploadSub = this.bulkUploadService.uploadDocument(
              documentDetails.file,
              documentDetails.fileId,
              documentDetails.organizationId,
              this.organizationType
            ).pipe(
              tap(res => {
                if (res.isError) {
                  documentDetails.uploadStatus = UPLOADSTATUS.ERROR;
                } else {
                  documentDetails.uploadStatus = UPLOADSTATUS.SUCCESS;
                  documentDetails.serverFileName = res.data.fileName;
                }
                // Remove subscription from map when complete
                this.uploadSubscriptions.delete(documentDetails);
              })
            );

            return uploadSub;
          },
          this.maxConcurrentUploads // Concurrency limit: only N files upload at once
        ),
        finalize(() => {
          // Check if all uploads are complete
          if (!this.files.some(d => d.uploadStatus === UPLOADSTATUS.PENDING)) {
            const successCount = this.files.filter(f => f.uploadStatus === UPLOADSTATUS.SUCCESS).length;
            const errorCount = this.files.filter(f => f.uploadStatus === UPLOADSTATUS.ERROR).length;
            this.uploadingEvent.emit(false);
          }
        })
      )
      .subscribe({
        error: (err) => {
          console.error('Upload error:', err);
          // Emit false even on error to stop loading state
          this.uploadingEvent.emit(false);
        }
      });
  }

  public get uploadStatus(): typeof UPLOADSTATUS {
    return UPLOADSTATUS;
  }

  onRemove(event: any) {
    // Cancel upload if it's in progress
    this.cancelUpload(event);

    this.files.splice(this.files.indexOf(event), 1);
    this.onFileRemove.emit(this.files.length);
  }

  cancelUpload(documentDetails: DocumentModel) {
    const subscription = this.uploadSubscriptions.get(documentDetails);
    if (subscription) {
      subscription.unsubscribe();
      this.uploadSubscriptions.delete(documentDetails);
      documentDetails.uploadStatus = UPLOADSTATUS.ERROR;
      this.toastService.error(`Upload cancelled for ${documentDetails.file.name}`, 'Upload Cancelled');

      // Check if all uploads are complete
      if (!this.files.some(d => d.uploadStatus === UPLOADSTATUS.PENDING)) {
        this.uploadingEvent.emit(false);
      }
    }
  }

  retryUpload(documentDetails: DocumentModel) {
    // Reset status to pending
    documentDetails.uploadStatus = UPLOADSTATUS.PENDING;
    this.uploadingEvent.emit(true);

    // Upload the file (with stored organizationId and organizationType)
    const subscription = this.bulkUploadService.uploadDocument(
      documentDetails.file,
      documentDetails.fileId,
      documentDetails.organizationId,
      this.organizationType
    ).pipe(
        tap(res => {
          if (res.isError) {
            documentDetails.uploadStatus = UPLOADSTATUS.ERROR;
          } else {
            documentDetails.uploadStatus = UPLOADSTATUS.SUCCESS;
            documentDetails.serverFileName = res.data.fileName;
          }
          // Remove subscription from map when complete
          this.uploadSubscriptions.delete(documentDetails);

          // Check if all uploads are complete
          if (!this.files.some(d => d.uploadStatus === UPLOADSTATUS.PENDING)) {
            this.uploadingEvent.emit(false);
          }
        })
      )
      .subscribe({
        error: (err) => {
          console.error('Retry upload error:', err);
          documentDetails.uploadStatus = UPLOADSTATUS.ERROR;
          this.uploadSubscriptions.delete(documentDetails);

          // Check if all uploads are complete
          if (!this.files.some(d => d.uploadStatus === UPLOADSTATUS.PENDING)) {
            this.uploadingEvent.emit(false);
          }
        }
      });

    // Store subscription for potential cancellation
    this.uploadSubscriptions.set(documentDetails, subscription);
  }

  getStatusIcon(status: UPLOADSTATUS): string {
    switch (status) {
      case UPLOADSTATUS.QUEUED:
        return 'schedule';
      case UPLOADSTATUS.PENDING:
        return 'hourglass_empty';
      case UPLOADSTATUS.SUCCESS:
        return 'check_circle';
      case UPLOADSTATUS.ERROR:
        return 'error';
      default:
        return 'insert_drive_file';
    }
  }

  getStatusClass(status: UPLOADSTATUS): string {
    switch (status) {
      case UPLOADSTATUS.QUEUED:
        return 'status-queued';
      case UPLOADSTATUS.PENDING:
        return 'status-pending';
      case UPLOADSTATUS.SUCCESS:
        return 'status-success';
      case UPLOADSTATUS.ERROR:
        return 'status-error';
      default:
        return '';
    }
  }

  // Getting uploaded file type to apply icon
  getFileType(fileType: any) {
    if (fileType) {
      var fileType = fileType.split('/');
      return (fileType = fileType[1]);
    }
    else {
      return 'general-file';
    }
  }

  public clearFiles() {
    this.files = [];
    this.onFileRemove.emit(this.files.length);
  }

  // Clear all files regardless of status
  clearAll(): void {
    this.files = [];
    this.onFileRemove.emit(this.files.length);
  }

  // Clear only successfully uploaded files
  clearSuccessful(): void {
    this.files = this.files.filter(f => f.uploadStatus !== UPLOADSTATUS.SUCCESS);
    this.onFileRemove.emit(this.files.length);
  }

  // Check if there are any successful uploads
  hasSuccessfulUploads(): boolean {
    return this.files.some(f => f.uploadStatus === UPLOADSTATUS.SUCCESS);
  }

  // Get count of files that are NOT successfully uploaded (these count against the limit)
  get activeFileCount(): number {
    return this.files.filter(f => f.uploadStatus !== UPLOADSTATUS.SUCCESS).length;
  }

  get successCount(): number {
    return this.files.filter(f => f.uploadStatus === UPLOADSTATUS.SUCCESS).length;
  }

  get failedCount(): number {
    return this.files.filter(f => f.uploadStatus === UPLOADSTATUS.ERROR).length;
  }

  get pendingCount(): number {
    return this.files.filter(f => f.uploadStatus === UPLOADSTATUS.PENDING).length;
  }

  get queuedCount(): number {
    return this.files.filter(f => f.uploadStatus === UPLOADSTATUS.QUEUED).length;
  }
}
